import React from "react";
import { useNavigate } from "react-router-dom";
import { Users, Video, Radio, ChevronRight, Settings } from "lucide-react";
import { useWhiteLabel } from "../../../context/WhiteLabelContext";
import { useSiteContext } from "../../../context/SiteContext";
import RLinkLogo from "../../common/RLinkLogo";

const sessionTypes = [
  {
    id: "meeting",
    title: "Meeting",
    description:
      "Host collaborative video meetings with your team or clients. Perfect for discussions, brainstorming, and team syncs.",
    icon: Users,
    features: [
      "Up to 100 participants",
      "Screen sharing",
      "Chat & reactions",
      "Recording",
    ],
    gradient: "from-purple-600 to-indigo-600",
    buttonGradient: "from-[#6a1fbf] to-[#00c853]",
  },
  {
    id: "webinar",
    title: "Webinar",
    description:
      "Present to large audiences with professional broadcasting tools. Ideal for training, demos, and presentations.",
    icon: Video,
    features: [
      "Q&A & polls",
      "Slides integration",
      "Registration",
      "Built-in conversion tools",
    ],
    gradient: "from-indigo-600 to-blue-600",
    buttonGradient: "from-[#6a1fbf] to-[#00c853]",
  },
  {
    id: "livestream",
    title: "Live Stream",
    description:
      "Broadcast to YouTube, Facebook, and custom RTMP destinations. Go live to your audience anywhere.",
    icon: Radio,
    features: [
      "Multi-platform",
      "Real-time overlays",
      "Custom branding",
      "Analytics",
    ],
    gradient: "from-blue-600 to-cyan-600",
    buttonGradient: "from-[#6a1fbf] to-[#00c853]",
  },
];

const StartLanding = () => {
  const navigate = useNavigate();
  const { branding: wlBranding } = useWhiteLabel();
  const { user } = useSiteContext();
  const isBasicPlan = user?.plan?.toLowerCase() === 'basic';

  const handleStartSession = (type) => {
    navigate(`/dashboard/setup?type=${type}`);
  };

  const handleGoToAdmin = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#001233] text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 md:p-6 lg:p-8">
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <RLinkLogo size="md" companyLogo={wlBranding.companyLogo} companyName={wlBranding.companyName} />
          </div>

          <button
            onClick={handleGoToAdmin}
            className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors font-sans"
          >
            <Settings className="w-4 h-4" />
            <span className="text-xs md:text-sm font-medium">Admin</span>
          </button>
        </nav>
      </header>

      {/* Hero */}
      <main className="relative z-10 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center pt-8 md:pt-12 pb-10 md:pb-16">
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold tracking-tight mb-4 md:mb-6 font-sans">
              <span className="bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent">
                {wlBranding.companyName || 'R-Link Studio'}
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#6a1fbf] to-[#00c853] bg-clip-text text-transparent">
                Made Simple
              </span>
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto px-4 font-sans">
              Create stunning live streams, webinars, and video meetings with
              our all-in-one broadcasting studio. No downloads required.
            </p>
          </div>

          {/* Session Type Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 pb-12 md:pb-20">
            {sessionTypes.map((type, index) => {
              const IconComponent = type.icon;
              const isDisabled = isBasicPlan && (type.id === 'webinar' || type.id === 'livestream');
              return (
                <div
                  key={type.id}
                  className={`group relative ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}
                  style={{ animationDelay: `${0.5 + index * 0.1}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#6a1fbf]/20 to-transparent rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 h-full hover:border-[#6a1fbf]/50 transition-all duration-300 hover:-translate-y-2">
                    <div
                      className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${type.gradient} flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <IconComponent className="w-6 h-6 md:w-7 md:h-7 text-white" />
                    </div>

                    <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-3 font-sans">
                      {type.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-400 mb-4 md:mb-6 leading-relaxed font-sans">
                      {type.description}
                    </p>

                    <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                      {type.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-2 text-xs md:text-sm text-gray-300 font-sans"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      disabled={isDisabled}
                      onClick={() => handleStartSession(type.id)}
                      className={`inline-flex items-center gap-2 w-full justify-center py-3 md:py-4 px-4 md:px-6 rounded-xl text-sm md:text-base font-semibold bg-gradient-to-r ${type.buttonGradient} hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 font-sans ${isDisabled ? 'cursor-not-allowed' : ''}`}
                    >
                      <span>Start {type.title}</span>
                      <ChevronRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StartLanding;
